import { memo, useCallback } from 'react'
import { UpcomingUnlocksBaseProps, UnlockItem } from './UpcomingUnlocksBase'
import { ProjectLogo } from './ProjectLogo'

const UnlockItemComponent = memo(({ 
  item, 
  onItemClick,
  onDetailsClick
}: { 
  item: UnlockItem
  onItemClick?: (item: UnlockItem) => void
  onDetailsClick?: (item: UnlockItem) => void
}) => {
  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])

  const handleDetailsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onDetailsClick?.(item)
  }, [item, onDetailsClick])

  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[16px] items-start pl-[12px] pr-[18px] py-[12px] cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
      onClick={handleClick}
    >
      {/* Row 1: Logo + Token info */}
      <div className="flex gap-[10px] items-start w-full">
        <ProjectLogo logo={item.logo} color={item.color} alt={item.name} />
        <div className="flex gap-[8px] items-baseline leading-[0] flex-1 min-w-0">
          <span className="font-plus-jakarta font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] truncate">
            {item.name}
          </span>
          <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-nowrap shrink-0">
            in {item.days} days
          </span>
          <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] whitespace-nowrap shrink-0">
            (+{item.value}, {item.percentage}%)
          </span>
        </div>
      </div>

      {/* Row 2: Buttons */}
      <div className="flex gap-[8px] items-center w-full">
        {/* Details button - flex-1 to take remaining space */}
        <button
          onClick={handleDetailsClick}
          className="bg-[rgba(45,41,35,0.1)] dark:bg-[rgba(253,253,253,0.1)] flex flex-1 items-center justify-center px-[16px] py-[8px]"
        >
          <span className="font-plus-jakarta font-medium text-[12px] leading-[16px] text-[#2d2923] dark:text-[#fdfdfd]">
            Details
          </span>
        </button>
      </div>
    </div>
  )
})

UnlockItemComponent.displayName = 'UnlockItemComponent'

export const UpcomingUnlocksMobile = ({ 
  items, 
  onItemClick,
  onDetailsClick,
  // onMoreDetailsClick - in Figma this is outside the component
  loading = false
}: UpcomingUnlocksBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          Upcoming Unlocks
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading upcoming unlocks...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          Upcoming Unlocks
        </p>
      </div>

      {/* Unlock items list */}
      <div className="flex-1 overflow-y-auto space-y-3 scrollbar-hide">
        {items.map((item) => (
          <UnlockItemComponent
            key={item.id}
            item={item}
            onItemClick={onItemClick}
            onDetailsClick={onDetailsClick}
          />
        ))}

        {items.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No upcoming unlocks
          </div>
        )}
      </div>
    </div>
  )
}
